function resolveBaseUrl(): string {
  const base = import.meta.env.VITE_ADMIN_API_URL ?? "";
  if (typeof base === "string" && base.length > 0) return base.replace(/\/$/, "");
  return "";
}

export type AdminListResponse = {
  records: Record<string, unknown>[];
  total: number;
  page: number;
  per_page: number;
};

export type AdminListParams = {
  page?: number;
  per_page?: number;
  sort_by?: string;
  sort_dir?: "asc" | "desc";
  search?: string;
};

function buildQuery(params: AdminListParams): string {
  const sp = new URLSearchParams();
  if (params.page) sp.set("page", String(params.page));
  if (params.per_page) sp.set("per_page", String(params.per_page));
  if (params.sort_by) sp.set("sort_by", params.sort_by);
  if (params.sort_dir) sp.set("sort_dir", params.sort_dir);
  if (params.search) sp.set("search", params.search);
  const qs = sp.toString();
  return qs ? `?${qs}` : "";
}

async function adminGet<T>(path: string): Promise<T> {
  const response = await fetch(`${resolveBaseUrl()}/api/admin${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Admin API error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function adminPatch<T>(path: string, data: Record<string, unknown>): Promise<T> {
  const response = await fetch(`${resolveBaseUrl()}/api/admin${path}`, {
    method: "PATCH",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error(`Admin API error: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

async function adminDelete(path: string): Promise<void> {
  const response = await fetch(`${resolveBaseUrl()}/api/admin${path}`, {
    method: "DELETE",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Admin API error: ${response.status}`);
  }
}

// Per-resource API functions
const resources = [
  "logins",
  "rooms",
  "hands",
  "players",
  "room-events",
  "player-relationships",
  "chat-messages",
  "user-stats",
  "admin-logins",
  "player-requests",
] as const;

export type AdminResource = (typeof resources)[number];

export function fetchAdminList(
  resource: AdminResource,
  params: AdminListParams,
): Promise<AdminListResponse> {
  return adminGet<AdminListResponse>(`/${resource}${buildQuery(params)}`);
}

export function fetchAdminRecord(
  resource: AdminResource,
  id: number | string,
): Promise<Record<string, unknown>> {
  return adminGet<Record<string, unknown>>(`/${resource}/${id}`);
}

export function updateAdminRecord(
  resource: AdminResource,
  id: number | string,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  return adminPatch<Record<string, unknown>>(`/${resource}/${id}`, data);
}

export function deleteAdminRecord(resource: AdminResource, id: number | string): Promise<void> {
  return adminDelete(`/${resource}/${id}`);
}

// Auth API
export type AuthMeResponse = {
  id: number;
  email: string;
  display_name: string;
  is_admin: boolean;
};

export async function fetchAuthMe(): Promise<AuthMeResponse> {
  const base = resolveBaseUrl();
  const response = await fetch(`${base}/api/auth/me`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) {
    throw new Error(`Auth error: ${response.status}`);
  }
  return response.json() as Promise<AuthMeResponse>;
}

export async function loginWithGoogle(credential: string): Promise<AuthMeResponse> {
  const base = resolveBaseUrl();
  const response = await fetch(`${base}/api/auth/google`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ credential }),
  });
  if (!response.ok) {
    throw new Error(`Auth error: ${response.status}`);
  }
  return response.json() as Promise<AuthMeResponse>;
}
